import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import parse from 'html-react-parser';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { paymentActions } from '@/redux/actions';
import { paymentSelectors, profileSelectors } from '@/redux/selectors';
import { COMMON_CONTAINER_ID } from '@/config';
import {
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
} from '@/utils';
import { useOpenChat } from '@/hooks';
import { InvoiceRequest } from '@/api';
import { getIconByAsset, getShortAddress } from '@/helpers';
import { AlertBox, Button, Input, QrCodeImage } from '@/components';

import style from './CryptoCheckoutForm.module.sass';

type CheckoutFormProps = {
  planId: number;
  currency: string;
  tariffType?: string;
};

const CryptoCheckoutForm = ({
  planId,
  currency,
  tariffType,
}: CheckoutFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { cryptoInvoice, cryptoInvoiceError, paymentLoading } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );
  const isAccountLiteMode = useAppSelector(profileSelectors.isAccountLiteMode);
  const [prevCurrency, setPrevCurrency] = useState(currency);
  const [prevPlanId, setPrevPlanId] = useState(planId);
  const { handleOpenChat } = useOpenChat();

  useEffect(() => {
    if (cryptoInvoiceError) {
      toast.error(cryptoInvoiceError, {
        containerId: COMMON_CONTAINER_ID,
      });
    }

    return () => {
      dispatch(paymentActions.setCryptoInvoiceError(null));
    };
  }, [cryptoInvoiceError]);

  useEffect(() => {
    setPrevCurrency(currency);
    setPrevPlanId(planId);

    return () => {
      dispatch(paymentActions.setCryptoInvoice(null));
      dispatch(paymentActions.setErrorCode(null));
    };
  }, [currency, planId]);

  const performSubmit = async () => {
    let gClientId;
    let gSessionId;

    try {
      gClientId = await getGoogleAnalyticsClientId();
      gSessionId = await getGoogleAnalyticsSessionId();
    } catch (e) {
      console.log(e);
    }

    const payload: InvoiceRequest = {
      type: 'plan',
      plan: planId,
      asset: currency,
      gClientId,
      gSessionId,
    };
    dispatch(paymentActions.createCryptoInvoice(payload));
  };

  const createCryptoInvoice = async () => {
    if (isAccountLiteMode) {
      handleOpenChat();
    } else {
      performSubmit();
    }
  };

  if (!cryptoInvoice || prevCurrency !== currency || prevPlanId !== planId) {
    return (
      <Button
        className={style.btn}
        onClick={createCryptoInvoice}
        isLoading={paymentLoading}
      >
        {t('translation.generate_invoice')}
      </Button>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.qrCodeContainer}>
        <span className={style.tittle}>
          {t('translation.invoice_created_done_str')}
        </span>
        <QrCodeImage address={cryptoInvoice.invoice.address} />
      </div>

      <div className={style.totalContainer}>
        <span className={style.text_sm}>
          {t('translation.invoices_form_total_str')}
        </span>
        <div className={style.amount}>
          <img src={getIconByAsset(currency)} alt="Currency icon" />
          <span>{`${currency} ${cryptoInvoice.invoice.amount}`}</span>
        </div>
      </div>

      <Input
        id="adress"
        name="adress"
        type="text"
        value={getShortAddress(cryptoInvoice.invoice.address)}
        onChange={() => {}}
        onBlur={() => {}}
        className={style.copyableInput}
        fullCopyableValue={cryptoInvoice.invoice.address}
        placeholder="Adress"
        readOnly
        copiable
      />
      <AlertBox type="warning" className={style.alert}>
        {{
          description: parse(t('translation.invoice_created_aml_warning_str')),
          icon: '⚠️',
        }}
      </AlertBox>

      <AlertBox type="warning" className={style.alert}>
        {{
          description: parse(t('translation.invoice_created_info_amount_str')),
          icon: '⚠️',
        }}
      </AlertBox>

      <AlertBox type="info" className={style.alert}>
        {{
          description: parse(t('translation.invoice_generated_msg')),
          icon: '👍',
        }}
      </AlertBox>

      <AlertBox type="info" className={style.alert}>
        {{
          description: parse(
            t('translation.invoice_crypto_expires_at', {
              date: new Date(
                Number(cryptoInvoice.invoice.dueTime) * 1000,
              ).toLocaleString(),
            }),
          ),
          icon: '⏰',
        }}
      </AlertBox>
    </div>
  );
};

export default CryptoCheckoutForm;
